import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, connect } from 'react-redux';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CategoryDialog from './CategoryDialog';
import { Button, Typography } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert, { AlertColor } from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import { getRestaurants } from '@utilities/auth';
import { updateCollection } from '@src/utilities/auth';
import { ref, uploadBytesResumable, getDownloadURL, getStorage, deleteObject } from 'firebase/storage';
import DeleteIcon from '@mui/icons-material/RemoveCircleOutline';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import ImageIcon from '@mui/icons-material/Image';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

import './Inventory.scss';
import NoDataFound from '@src/app/components/NoDataFound';

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/gif,image/webp';
const GCS_INVENTORY_PREFIX = 'inventory-images';

const DEFAULT_SUB_CATEGORY = {
    image: '',
    order: 9,
    available: true,
    inclusiveTax: false,
    hideInOnlineMenu: false,
    type: '',
    variants: [{ size: '', cost: '' }],
};

/** Deep clone so local edits never mutate Redux `common.restaurants` / `selectedRestaurant.menu`. */
function cloneInventoryMenu(menu: any): any {
    if (!menu || typeof menu !== 'object') return {};
    return JSON.parse(JSON.stringify(menu));
}

function getStoragePathFromDownloadUrl(downloadUrl: string): string | null {
    try {
        const u = new URL(downloadUrl);
        if (!u.origin.includes('firebasestorage.googleapis.com')) return null;
        const match = u.pathname.match(/\/o\/(.+)$/);
        if (!match) return null;
        const path = decodeURIComponent(match[1]);
        return path.startsWith(GCS_INVENTORY_PREFIX) ? path : null;
    } catch {
        return null;
    }
}

function buildInventoryImagePath(
    restaurantId: string,
    category: string,
    subCategory: string,
    file: File
): string {
    const sanitize = (s: string) => (s || '').replace(/[\W\s]+/g, '-').replace(/^-|-$/g, '') || 'item';
    const ext = file.name?.includes('.')
        ? file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
        : '.jpg';
    const timestamp = Date.now();
    const base = `${sanitize(category)}-${sanitize(subCategory)}-${timestamp}${ext}`;
    return `${GCS_INVENTORY_PREFIX}/${restaurantId}/${base}`;
}

const Inventory = ({ selectedRestId, inventory, categoryOrder, roles }: any) => {
    const dispatch = useDispatch();
    const [open, setOpen] = React.useState(false);
    const [cat, setCat] = useState('');
    const [subCat, setSubCat] = useState('');
    const [copyMenu, setCopyMenu] = useState<any>(() => cloneInventoryMenu(inventory));
    const [copyCatOrder, setCopyCatOrder] = useState([...categoryOrder]);
    const [updateData, setUpdateData] = React.useState({});
    const [checked, setChecked] = React.useState(true);

    interface Notification {
        message: string;
        severity: AlertColor;
    }

    const [openNotification, setOpenNotification] = React.useState(false);
    const [notification, setNotification] = React.useState<Notification>({
        message: 'Successful',
        severity: 'success',
    });
    const [imageError, setImageError] = React.useState<string | null>(null);
    const [imageUploadProgress, setImageUploadProgress] = React.useState<number | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const storage = getStorage();

    const currentItem = cat && subCat ? copyMenu?.[cat]?.[subCat] : null;

    const handleNotificationClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setOpenNotification(false);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (cat && subCat) {
            setChecked(event?.target?.checked);
            setCopyMenu({
                ...copyMenu,
                [cat]: {
                    ...copyMenu[cat],
                    [subCat]: { ...copyMenu[cat][subCat], available: event?.target?.checked },
                },
            });
        }
    };

    const handleInclusiveTaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!cat || !subCat) return;
        setCopyMenu({
            ...copyMenu,
            [cat]: {
                ...copyMenu[cat],
                [subCat]: { ...copyMenu[cat][subCat], inclusiveTax: event.target.checked },
            },
        });
    };

    const handleHideInOnlineMenuChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!cat || !subCat) return;
        setCopyMenu({
            ...copyMenu,
            [cat]: {
                ...copyMenu[cat],
                [subCat]: { ...copyMenu[cat][subCat], hideInOnlineMenu: event.target.checked },
            },
        });
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, variants?: any, index?: any) => {
        const { name, value } = event?.target;
        if (variants) {
            const currentVariants = copyMenu[cat][subCat][variants] || [];
            const updatedVariants = currentVariants.map((variant: Record<string, unknown>, i: number) =>
                i === index ? { ...variant, [name]: value } : variant
            );
            cat && subCat && setCopyMenu({
                ...copyMenu,
                [cat]: { ...copyMenu[cat], [subCat]: { ...copyMenu[cat][subCat], [variants]: updatedVariants } },
            });
        } else {
            cat && subCat && setCopyMenu({
                ...copyMenu,
                [cat]: { ...copyMenu[cat], [subCat]: { ...copyMenu[cat][subCat], [name]: value || '' } },
            });
        }
    };

    const deleteInventoryImageFromStorage = (imageUrl: string) => {
        const path = getStoragePathFromDownloadUrl(imageUrl);
        if (!path) return Promise.resolve();
        const storageRef = ref(storage, path);
        return deleteObject(storageRef).catch((err) => {
            console.warn('GCS delete failed (may already be removed):', err);
        });
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        setImageError(null);
        const file = event.target.files?.[0];
        if (!file || !cat || !subCat || !selectedRestId) return;
        if (!file.type.startsWith('image/')) {
            setImageError('Please select an image file (JPEG, PNG, GIF, or WebP).');
            return;
        }
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            setImageError(`Image must be 5MB or smaller (current: ${(file.size / 1024 / 1024).toFixed(2)}MB).`);
            return;
        }
        const previousImageUrl = copyMenu[cat]?.[subCat]?.image || '';
        const storagePath = buildInventoryImagePath(selectedRestId, cat, subCat, file);
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, file);
        setImageUploadProgress(0);
        uploadTask.on(
            'state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setImageUploadProgress(progress);
            },
            (err) => {
                console.error('Inventory image upload error:', err);
                setImageError('Failed to upload image. Please try again.');
                setImageUploadProgress(null);
            },
            async () => {
                try {
                    const url = await getDownloadURL(uploadTask.snapshot.ref);
                    setCopyMenu({
                        ...copyMenu,
                        [cat]: {
                            ...copyMenu[cat],
                            [subCat]: { ...copyMenu[cat][subCat], image: url },
                        },
                    });
                    await deleteInventoryImageFromStorage(previousImageUrl);
                } catch (err) {
                    console.error('Failed to get download URL:', err);
                    setImageError('Upload completed but failed to get image URL.');
                }
                setImageUploadProgress(null);
            }
        );
        event.target.value = '';
    };

    const handleRemoveImage = () => {
        if (!cat || !subCat) return;
        const currentImageUrl = copyMenu[cat][subCat]?.image || '';
        setCopyMenu({
            ...copyMenu,
            [cat]: { ...copyMenu[cat], [subCat]: { ...copyMenu[cat][subCat], image: '' } },
        });
        setImageError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        deleteInventoryImageFromStorage(currentImageUrl);
    };

    const setSubCatHandle = (sCatVal: any) => {
        setSubCat(sCatVal);
        if (cat && sCatVal) {
            setChecked(copyMenu[cat][sCatVal].available);
        }
    };

    const addNewCatSubCat = (type: any) => {
        setUpdateData({ type, initialValue: '', updatedValue: '' });
        setOpen(true);
    };

    const addVariant = (_catA: any, _subCatA: any) => {
        cat && subCat && setCopyMenu({
            ...copyMenu,
            [cat]: {
                ...copyMenu[cat],
                [subCat]: {
                    ...copyMenu[cat][subCat],
                    variants: [...copyMenu[cat][subCat].variants, { size: '', cost: '' }],
                },
            },
        });
    };

    const removeVariant = (catA: any, subCatA: any, keyVariant: any) => {
        const variantsTemp = copyMenu[cat][subCat].variants
            .filter((_: unknown, i: number) => i !== keyVariant)
            .filter((val: unknown) => typeof val);
        catA && subCatA && setCopyMenu({
            ...copyMenu,
            [catA]: { ...copyMenu[catA], [subCatA]: { ...copyMenu[catA][subCatA], variants: variantsTemp } },
        });
    };

    const handleChangeCatSubCat = (type: any, key: any) => {
        setUpdateData({ type, initialValue: key || '', updatedValue: key || '' });
        setOpen(true);
    };

    const getUpdatedKey = (str: any) => str.replace(/[\W\s]+/g, '-');

    const handleClose = (updatedData: any) => {
        setOpen(false);
        switch (updatedData?.type) {
            case 'newCat': {
                const strKey = getUpdatedKey(updatedData?.updatedValue);
                if (strKey && copyMenu[strKey]) setCat(strKey);
                if (strKey && !copyMenu[strKey]) {
                    setCopyMenu({ ...copyMenu, [strKey]: {} });
                    setCat(strKey);
                    if (copyCatOrder.indexOf(strKey) === -1) {
                        setCopyCatOrder([...copyCatOrder, strKey]);
                    }
                }
                break;
            }
            case 'newSubCat': {
                const strKey = getUpdatedKey(updatedData?.updatedValue);
                if (cat && strKey && copyMenu[cat][strKey]) setSubCat(strKey);
                if (cat && strKey && !copyMenu[cat][strKey]) {
                    setCopyMenu({
                        ...copyMenu,
                        [cat]: { ...copyMenu[cat], [strKey]: { ...DEFAULT_SUB_CATEGORY } },
                    });
                    setSubCat(strKey);
                }
                break;
            }
            case 'updateCat': {
                const strKey = getUpdatedKey(updatedData?.updatedValue);
                if (strKey && !copyMenu[strKey]) {
                    const tempMenu = { ...copyMenu };
                    const tempCopyCatOrder = [...copyCatOrder];
                    tempMenu[strKey] = tempMenu[updatedData?.initialValue];
                    delete tempMenu[updatedData?.initialValue];
                    if (tempCopyCatOrder?.indexOf(updatedData?.initialValue) !== -1) {
                        tempCopyCatOrder[tempCopyCatOrder?.indexOf(updatedData?.initialValue)] = strKey;
                    }
                    setCopyMenu(tempMenu);
                    setCat(strKey);
                    setCopyCatOrder(tempCopyCatOrder);
                }
                break;
            }
            case 'updateSubCat': {
                const strKey = getUpdatedKey(updatedData?.updatedValue);
                if (cat && strKey && !copyMenu[cat][strKey]) {
                    const tempMenu = { ...copyMenu };
                    tempMenu[cat][strKey] = tempMenu[cat][updatedData?.initialValue];
                    delete tempMenu[cat][updatedData?.initialValue];
                    setCopyMenu(tempMenu);
                    setSubCat(strKey);
                }
                break;
            }
            case 'removeCat': {
                if (updatedData?.updatedValue) {
                    const copyTemp = { ...copyMenu };
                    const tempCopyCatOrder = [...copyCatOrder];
                    delete copyTemp[updatedData?.updatedValue];
                    if (tempCopyCatOrder?.indexOf(updatedData?.updatedValue) !== -1) {
                        delete tempCopyCatOrder[tempCopyCatOrder?.indexOf(updatedData?.initialValue)];
                    }
                    setCopyMenu({ ...copyTemp });
                    setCat('');
                    setSubCat('');
                    setCopyCatOrder(tempCopyCatOrder);
                }
                break;
            }
            case 'removeSubCat': {
                if (cat && updatedData?.updatedValue) {
                    const copyTemp = { ...copyMenu };
                    delete copyTemp[cat][updatedData?.updatedValue];
                    setCopyMenu({ ...copyTemp });
                    setSubCat('');
                }
                break;
            }
            default:
                break;
        }
    };

    const submitChanges = () => {
        const restaurantsCollectionName = 'restaurants';
        let temp = [...copyCatOrder];
        temp = temp.filter((val: any) => typeof val === 'string');
        if (selectedRestId) {
            updateCollection(restaurantsCollectionName, selectedRestId, 'menu', copyMenu);
            updateCollection(restaurantsCollectionName, selectedRestId, 'categoryOrder', temp);
        }
        setNotification({ message: 'Successfully Saved!', severity: 'success' });
        setOpenNotification(true);
        getRestaurants(dispatch, roles, { silent: true, preserveSelectedRestId: selectedRestId });
    };

    useEffect(() => {
        getRestaurants(dispatch, roles, { silent: true, preserveSelectedRestId: selectedRestId });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh restaurant/menu once when inventory opens
    }, []);

    useEffect(() => {
        setCopyMenu(cloneInventoryMenu(inventory));
        setCopyCatOrder([...categoryOrder]);
    }, [inventory, categoryOrder]);

    useEffect(() => {
        setImageError(null);
        setImageUploadProgress(null);
        if (cat && subCat && copyMenu[cat][subCat] && copyMenu[cat][subCat].available) {
            setChecked(copyMenu[cat][subCat].available);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: sync checked from cat/subCat/copyMenu
    }, [subCat]);

    const renderListItem = (label: string, isActive: boolean, onSelect: () => void, onEdit: () => void, onRemove: () => void) => (
        <li key={label} className={`inventory-list-item ${isActive ? 'active' : ''}`} onClick={onSelect}>
            <div className="inventory-list-item__inner">
                <span className="inventory-list-item__name">{label}</span>
                <div className="inventory-list-item__actions">
                    <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={(e) => { e.stopPropagation(); onEdit(); }}
                        startIcon={<EditRoundedIcon fontSize="small" />}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); onRemove(); }}
                    >
                        ×
                    </Button>
                </div>
            </div>
        </li>
    );

    const renderPanelHeader = (
        title: string,
        options: {
            showBack?: boolean;
            onBack?: () => void;
            onAdd?: () => void;
            addDisabled?: boolean;
            saveLabel?: string;
            saveAlways?: boolean;
        } = {}
    ) => (
        <div className="inventory-panel__header">
            {options.showBack && (
                <Button variant="text" color="primary" className="mob-btn" onClick={options.onBack} aria-label="Go back">
                    <KeyboardBackspaceIcon />
                </Button>
            )}
            <Typography component="h2" className="inventory-panel__title">{title}</Typography>
            <div className="inventory-panel__actions">
                {options.onAdd && (
                    <Button size="small" variant="outlined" color="primary" disabled={options.addDisabled} onClick={options.onAdd} startIcon={<AddRoundedIcon />}>
                        Add
                    </Button>
                )}
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    className={options.saveAlways ? 'inventory-panel__save' : 'mob-btn'}
                    onClick={submitChanges}
                    startIcon={<SaveRoundedIcon />}
                >
                    {options.saveLabel || 'Save'}
                </Button>
            </div>
        </div>
    );

    return (
        <>
            <Box className="inventory-page" sx={{ flexGrow: 1, overflow: 'hidden' }}>
                <Grid className="inventory-layout" container spacing={{ xs: 0, md: 0 }} columns={{ xs: 12, md: 12 }}>
                    {/* Categories */}
                    <Grid className={`animate block1 inventory-panel gapUp ${!cat && 'active'}`} xs={12} md={3}>
                        {renderPanelHeader('Categories', { onAdd: () => addNewCatSubCat('newCat') })}
                        {!!copyMenu && !!Object.keys(copyMenu).length ? (
                            <ul className="inventory-panel__body">
                                {Object.keys(copyMenu).map((val) =>
                                    renderListItem(
                                        val,
                                        val === cat,
                                        () => setCat(val),
                                        () => handleChangeCatSubCat('updateCat', val),
                                        () => handleChangeCatSubCat('removeCat', val)
                                    )
                                )}
                            </ul>
                        ) : (
                            <NoDataFound height="100%" title="No Category Yet – Start Adding One!" />
                        )}
                    </Grid>

                    {/* Sub-categories */}
                    <Grid className={`animate block2 inventory-panel gapUp ${cat && !subCat && 'active'}`} xs={12} md={3}>
                        {renderPanelHeader('Sub-cat', {
                            showBack: true,
                            onBack: () => setCat(''),
                            onAdd: () => addNewCatSubCat('newSubCat'),
                            addDisabled: !cat,
                        })}
                        {!!cat && !!copyMenu[cat] && !!Object.keys(copyMenu[cat]).length ? (
                            <ul className="inventory-panel__body">
                                {Object.keys(copyMenu[cat]).map((subVal) =>
                                    renderListItem(
                                        subVal,
                                        subVal === subCat,
                                        () => setSubCatHandle(subVal),
                                        () => handleChangeCatSubCat('updateSubCat', subVal),
                                        () => handleChangeCatSubCat('removeSubCat', subVal)
                                    )
                                )}
                            </ul>
                        ) : (
                            <NoDataFound height="100%" title="No Sub-Category Yet – Start Adding One!" />
                        )}
                    </Grid>

                    {/* Item details */}
                    <Grid className={`animate block3 inventory-panel gapUp ${cat && subCat && 'active'}`} xs={12} md={6}>
                        {renderPanelHeader(subCat ? `${subCat}` : 'Item details', {
                            showBack: true,
                            onBack: () => setSubCat(''),
                            saveLabel: 'Save changes',
                            saveAlways: true,
                        })}
                        <div className="inventory-item-form">
                            {cat && subCat && currentItem && (
                                <>
                                    <section className="inventory-card">
                                        <Typography className="inventory-card__title">Availability & tax</Typography>
                                        <div className="inventory-row inventory-row--between">
                                            <label className="inventory-switch-wrap">
                                                Out of stock
                                                <Switch
                                                    checked={!checked}
                                                    onChange={handleChange}
                                                    inputProps={{ 'aria-label': 'Out of stock' }}
                                                    color="primary"
                                                />
                                            </label>
                                            <TextField
                                                size="small"
                                                type="number"
                                                onChange={handleInputChange}
                                                name="customGST"
                                                value={currentItem.customGST || ''}
                                                label="GST %"
                                                sx={{ width: 120 }}
                                            />
                                            <div className="inventory-checkbox-group">
                                                <div className="inventory-checkbox-wrap">
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={Boolean(currentItem.inclusiveTax)}
                                                                onChange={handleInclusiveTaxChange}
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Inclusive Tax"
                                                    />
                                                </div>
                                                <div className="inventory-checkbox-wrap">
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={Boolean(currentItem.hideInOnlineMenu)}
                                                                onChange={handleHideInOnlineMenuChange}
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Hide In Online Menu"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </section>

                                    <section className="inventory-card inventory-image-section">
                                        <Typography className="inventory-card__title">Image</Typography>
                                        <TextField
                                            onChange={handleInputChange}
                                            name="image"
                                            value={currentItem.image || ''}
                                            fullWidth
                                            size="small"
                                            label="Image URL"
                                            placeholder="Paste URL or upload below"
                                        />
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept={ACCEPTED_IMAGE_TYPES}
                                            onChange={handleImageUpload}
                                            style={{ display: 'none' }}
                                            id="inventory-image-upload"
                                        />
                                        <div className="inventory-image-row">
                                            {currentItem.image && (
                                                <div className="inventory-image-preview-wrap">
                                                    <img
                                                        src={currentItem.image}
                                                        alt={subCat}
                                                        className="inventory-image-preview"
                                                        onError={() => setImageError('Failed to load image. Check URL or try another image.')}
                                                    />
                                                </div>
                                            )}
                                            <div className="inventory-image-actions">
                                                <Button
                                                    variant="outlined"
                                                    component="label"
                                                    htmlFor="inventory-image-upload"
                                                    startIcon={<ImageIcon />}
                                                    size="small"
                                                    disabled={imageUploadProgress !== null}
                                                >
                                                    {imageUploadProgress !== null ? 'Uploading…' : (currentItem.image ? 'Replace' : 'Upload (max 5MB)')}
                                                </Button>
                                                {currentItem.image && (
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        startIcon={<DeleteOutlineIcon />}
                                                        onClick={handleRemoveImage}
                                                    >
                                                        Remove
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        {imageUploadProgress !== null && (
                                            <LinearProgress variant="determinate" value={imageUploadProgress} className="inventory-image-progress" />
                                        )}
                                        {imageError && (
                                            <Alert severity="error" onClose={() => setImageError(null)} className="inventory-image-error">
                                                {imageError}
                                            </Alert>
                                        )}
                                    </section>

                                    <section className="inventory-card">
                                        <Typography className="inventory-card__title">Codes & sorting</Typography>
                                        <div className="inventory-field-grid">
                                            <TextField size="small" type="number" onChange={handleInputChange} name="sacCode" value={currentItem.sacCode || ''} fullWidth label="SAC Code" />
                                            <TextField size="small" type="number" onChange={handleInputChange} name="hsnCode" value={currentItem.hsnCode || ''} fullWidth label="HSN Code" />
                                            <TextField size="small" type="number" onChange={handleInputChange} name="order" value={currentItem.order ?? ''} label="Display order" />
                                            <TextField size="small" onChange={handleInputChange} name="type" value={currentItem.type || ''} label="Type" />
                                        </div>
                                    </section>

                                    <section className="inventory-card inventory-variants">
                                        <Typography className="inventory-card__title">Variants</Typography>
                                        {currentItem.variants?.map((it: any, keyVariant: any) => (
                                            <div className="inventory-variant-row" key={keyVariant}>
                                                <TextField
                                                    size="small"
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, 'variants', keyVariant)}
                                                    name="size"
                                                    value={it.size}
                                                    label="Size"
                                                />
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, 'variants', keyVariant)}
                                                    name="cost"
                                                    value={it.cost}
                                                    label="Cost"
                                                />
                                                <Button className="btnDelete" color="error" onClick={() => removeVariant(cat, subCat, keyVariant)}>
                                                    <DeleteIcon />
                                                </Button>
                                            </div>
                                        ))}
                                        <Button variant="outlined" color="secondary" onClick={() => addVariant(cat, subCat)} startIcon={<AddRoundedIcon />}>
                                            Add variant
                                        </Button>
                                    </section>

                                    <Button variant="contained" color="primary" fullWidth onClick={submitChanges} startIcon={<SaveRoundedIcon />}>
                                        Save changes
                                    </Button>
                                </>
                            )}
                        </div>
                    </Grid>
                </Grid>
                <CategoryDialog updateData={updateData} openDialog={open} onClose={handleClose} />
            </Box>
            <Snackbar open={openNotification} autoHideDuration={3000} onClose={handleNotificationClose}>
                <Alert onClose={handleNotificationClose} severity={notification?.severity} variant="filled" sx={{ width: '100%' }}>
                    {notification?.message}
                </Alert>
            </Snackbar>
        </>
    );
};

const mapStateToProps = (state: any) => {
    const { common, user } = state;
    return {
        inventory: common?.selectedRestaurant?.menu || {},
        categoryOrder: common?.selectedRestaurant?.categoryOrder || [],
        selectedRestId: common?.selectedRestaurant?.docId || '',
        roles: user?.user?.roles || {},
    };
};

export default connect(mapStateToProps)(Inventory);
